import { useMemo } from "react";

import { useLocation } from "react-router-dom";

import { useFacilitatorContext } from "./FacilitatorContextProvider";
import { ALL_PERIODS, periodCount, squadMatchesPeriod } from "./periods";
import {
  FIELD_CLASS,
  FIELD_LABEL_CLASS,
  Freshness,
  RetryControl,
  Skeleton,
  SkeletonRegion,
  StaleNotice,
} from "./primitives";
import { statusTerm } from "./statusVocabulary";

/**
 * THE PERSISTENT CONTEXT STRIP.
 *
 * One line, above every Facilitator OS surface, answering the three questions a
 * facilitator has to be able to answer before any number below it means
 * anything: which room is this, which slice of it am I looking at, and how old
 * are these numbers.
 *
 * WHAT IT MUST NEVER DO, and this is the A1 ruling in its most tempting place to
 * break: it must not present expected pace as a statement about where squads
 * are. Expected pace is the facilitator's own setting. The label says so, and an
 * assistive-technology reader gets the distinction spelled out, because "level 3"
 * sitting in a status bar is exactly the shape that reads as a measurement.
 */
export const FacilitatorContextBar = () => {
  const shell = useFacilitatorContext();
  const location = useLocation();

  /* THE SLICE LINE ONLY BELONGS WHERE THE SLICE IS VISIBLE. The period selector
     is shell-wide, but "showing 2 of 4 squads" is a statement about a squad
     list, and a surface that renders none was describing squads that were not
     on the page.

     PREPARE JOINED THIS LIST IN FE-9.5, and it belongs on it now for the reason
     it did not before. The command band renders a level spine and its tallies
     over the squad set on BOTH Dashboard modes, and as of this chunk that set
     is period-filtered on both (see `FacilitatorCommandBand`'s docblock and
     ADR-009). So Prepare does render a squad list: it renders it as a spine.
     Suppressing the control here while the filter went on applying was the
     other half of the same defect, and it was the half the facilitator
     experienced: numbers that changed with the mode and no control on screen
     that could explain them. */
  const showsSquads = [
    "/admin/dashboard",
    "/admin/prep",
    "/admin/deliverables",
  ].includes(location.pathname);

  /* WHICH SURFACES OWN THEIR OWN READ FAILURE. When the glance fails with no
     prior envelope, this strip and Coach's panel each rendered the same 403
     headline verbatim and each rendered its own "try again", both calling
     `shell.refetch`: one failure, two statements, two controls, at two sizes
     (D4, D5). The panel is the one with room to say what to do about it
     ("Pick another cohort, or ask whoever set up this room to add you"), so
     the panel keeps the recovery and this strip stands down entirely rather
     than restating a headline above it.

     IT CANNOT STAND DOWN EVERYWHERE, which is why this is a list and not a
     deletion. Manage, Review and Prepare each own their own reads and none of
     them recovers the SHELL read; on those surfaces this strip is the only
     control that can, and removing it would have left three surfaces with a
     failed context and no way back. */
  const panelOwnsReadFailure = ["/admin/dashboard"].includes(location.pathname);

  const envelope = shell?.envelope ?? null;
  const status = shell?.status ?? "loading";
  const period = shell?.period ?? ALL_PERIODS;
  const options = shell?.periodOptions ?? [];

  const squads = useMemo(
    () => (Array.isArray(envelope?.squads) ? envelope.squads : []),
    [envelope],
  );

  const filtered = useMemo(
    () => squads.filter((squad) => squadMatchesPeriod(squad?.sections, period)),
    [squads, period],
  );

  // Squads that appear in this filtered view AND in at least one other period.
  // They are not duplicated silently: the count is stated below the strip so a
  // facilitator reading "period 2" knows which of these squads they will also
  // meet under period 3.
  const spanning = useMemo(
    () =>
      period === ALL_PERIODS
        ? 0
        : filtered.filter((squad) => periodCount(squad?.sections) > 1).length,
    [filtered, period],
  );

  if (status === "loading" && !envelope) {
    return (
      <SkeletonRegion
        label="reading which room you are in"
        testId="facilitator-context-bar"
        className="flex items-center gap-x-3 border-b border-white/5 bg-background-midnight px-6 py-2.5"
      >
        <Skeleton className="h-4 w-48 rounded" />
        <Skeleton className="h-4 w-32 rounded" />
      </SkeletonRegion>
    );
  }

  if (status === "error" && !envelope) {
    if (panelOwnsReadFailure) return null;
    return (
      <div
        data-testid="facilitator-context-bar"
        role="status"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-white/5 bg-background-midnight px-6 py-2.5"
      >
        <span className="text-sm lowercase text-lv2">
          {shell?.error?.status === 403
            ? "this cohort is not available to you"
            : "the room context is not loading"}
        </span>
        <RetryControl inline onRetry={shell?.refetch} />
      </div>
    );
  }

  const challengeTitle = envelope?.challenge?.title || "";
  const cohortName = envelope?.cohort?.name || "";
  const stale = status === "error" && !!envelope;

  return (
    <div
      data-testid="facilitator-context-bar"
      className="flex flex-col gap-y-1 border-b border-white/5 bg-background-midnight px-6 py-2.5"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <p className="min-w-0 truncate text-sm text-lv1">
          {/* FE-9.6: the audit's no-cohort collision resolved in this bar's
              favour ("no cohorts assigned yet" on Coach retired to this), so
              this render is the one the vocabulary quotes. Reading it back from
              the module keeps that true rather than remembered. */}
          {cohortName || statusTerm("no_cohort")}
          {challengeTitle ? (
            <>
              <span aria-hidden="true" className="px-2 text-lv4">
                &middot;
              </span>
              <span className="text-charlotte">{challengeTitle}</span>
            </>
          ) : null}
        </p>

        {showsSquads && options.length > 0 ? (
          /* THE FIELD TREATMENT (S-2, P11). It was `text-xs px-2 py-1` on a
             flat `cloudburst` with a `white/10` border, which is exactly the
             shape Amir named as "reads as unstyled": nothing about it said it
             could receive anything. It becomes a recessed well (blackpearl,
             one step under the strip) with an inset top edge, a hairline that
             warms on hover, and a charlotte border on focus beside the ring.

             THE INSET LIVES IN A CLASS, NEVER INLINE, and that is a gate
             constraint rather than a style choice: `focusRing.utility.test.js`
             requires a focusable element's decorative shadow to stay
             overridable by the focus ring, and an inline `boxShadow` wins over
             the ring's own.

             8px OF VERTICAL PADDING, NOT P11's 11px, and the reason is that
             this control lives in the PERSISTENT strip above every OS surface
             rather than in a page body. 11px here grows the chrome on Review
             and Manage too, which were not that chunk's to change.

             AND THE 9.8c PARITY CHECK IS STRUCTURAL, NOT REMEMBERED. That draft
             expected Manage's inputs to take a fuller measure; the 9.8c plan
             ruled the other way and made byte-consistency with THIS treatment
             the acceptance condition, which is the stronger ruling because two
             hand-written copies of one treatment is the duplication this OS has
             already paid for three times over. So the string moved to
             `primitives/fieldClasses.js` and both sides import it. `lowercase`
             stays HERE rather than in the base: this select renders copy from a
             module, and Manage's search box renders what a teacher typed. */
          <label className="flex items-center gap-x-2">
            <span className={FIELD_LABEL_CLASS}>period</span>
            <select
              value={period}
              onChange={(event) => shell?.setPeriod(event.target.value)}
              className={`${FIELD_CLASS} lowercase`}
            >
              <option value={ALL_PERIODS}>all periods</option>
              {options.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {envelope && location.pathname === "/admin/dashboard" ? (
          <button
            type="button"
            role="switch"
            aria-checked={shell?.namesConcealed !== false}
            aria-label="conceal participant names in check-ins"
            onClick={() =>
              shell?.setNamesConcealed?.(shell?.namesConcealed === false)
            }
            className="focus-visible:focus-ring inline-flex items-center gap-x-2 rounded-full border border-charlotte/20 bg-background-cloudburst px-3 py-1 text-xs lowercase text-lv2 transition-all duration-fast hover:border-charlotte/40"
          >
            {/* THE KNOB'S OFFSET WAS APPLIED TWICE, and that is the whole
                defect. It was positioned `absolute` with `top` set and NO
                `left`, so its horizontal placement fell back to its static
                position, which the browser resolves to 14px here. Tailwind's
                `translate-x-3.5` then added its own 14px on top. 14 + 14 = 28,
                which is exactly the track's width, so the knob landed flush on
                the track's right edge and hung 12px past it, straight over the
                first letter of the label. That is how "names concealed"
                rendered as "ames concealed".

                Measured in a headless browser against the compiled stylesheet
                rather than reasoned about: track [13, 41], knob [41, 53],
                overflow +12px, and the label starts at 49, so the knob covered
                it. Two earlier hypotheses were wrong and are worth naming so
                nobody retries them: the track never shrank (it measured a
                correct 28px, so `flex-shrink` was not involved) and the label
                never wrapped.

                THE FIX REMOVES THE AMBIGUITY RATHER THAN COMPENSATING FOR IT.
                The knob is placed with `left` alone and carries no transform, so
                there is no static position to resolve and nothing to
                double-apply. `left-0.5` is 2px and `left-3.5` is 14px, which in
                a 28px track holding a 12px knob is a symmetric 2px inset at each
                end: measured [15, 27] off and [27, 39] on, both inside the
                track. Travel is animated on `left` instead of `transform`. */}
            <span
              aria-hidden="true"
              className={[
                "relative h-4 w-7 rounded-full transition-colors duration-fast",
                shell?.namesConcealed !== false
                  ? "bg-charlotte/50"
                  : "bg-background-astronaut",
              ].join(" ")}
            >
              <span
                className={[
                  "absolute top-0.5 h-3 w-3 rounded-full bg-text-lv0 transition-[left] duration-fast motion-reduce:transition-none",
                  shell?.namesConcealed !== false ? "left-3.5" : "left-0.5",
                ].join(" ")}
              />
            </span>
            <span data-testid="names-concealed-label">
              {shell?.namesConcealed !== false
                ? "names concealed"
                : "names visible"}
            </span>
          </button>
        ) : null}

        <div className="grow" />

        <Freshness generatedAt={envelope?.generated_at} />
      </div>

      {period !== ALL_PERIODS && showsSquads ? (
        /* THE SAME STAT SCALE AS THE BAND'S PACE LEVEL (S-3, P14), so the two
            numbers a facilitator reads before anything else on the screen are
            one instrument rather than two sizes. The sentence is unchanged
            character for character, spaces included, which is what keeps the
            three `toHaveTextContent` assertions in `FacilitatorShell.test.jsx`
            green; the numerals take the run's size and the words stay at the
            caption tier they belong to. The second clause is a scope note
            (P15), not a stat, so it does not scale. */
        <p className="text-xs text-lv3" role="status">
          showing{" "}
          <span className="text-[22px] font-semibold leading-none tabular-nums text-lv1">
            {filtered.length}
          </span>{" "}
          of <span className="tabular-nums">{squads.length}</span> squads.
          {spanning > 0
            ? ` ${spanning} ${spanning === 1 ? "of them also has" : "of them also have"} members in another period.`
            : ""}
        </p>
      ) : null}

      {stale ? <StaleNotice /> : null}
    </div>
  );
};

export default FacilitatorContextBar;
